/**
 * The arithmetic behind a **keyset**-paged list, with no React in it.
 *
 * ## Why this is not `pagination.ts`
 *
 * Every list route this app had read before Reporting is `limit`/`offset` paged
 * and answers `{ items, total, limit, offset }` — which is `PageState` exactly,
 * and which `Pager` steps through by adding and subtracting a page's worth. The
 * Reporting routes are not that. `GET /v1/expenses` and `GET /v1/reports/cash-book`
 * answer `{ items, limit, nextCursor }`: an opaque cursor and **no total at all**.
 *
 * Three things follow, and none of them is a variation on offset paging:
 *
 * - **There is no arithmetic.** A cursor is a value the server issued, stored and
 *   handed back verbatim. The document says so in as many words — "Opaque: do
 *   not construct one" — so the only thing this module does is remember them.
 * - **The end is announced rather than computed.** `nextCursor: null` means this
 *   was the last page, and it is "the only way to know, since a keyset page
 *   publishes no total". `hasNextPage` cannot be a sum here.
 * - **Going back has to be remembered.** The route issues a forward cursor and
 *   nothing else, so the way back to page two is the cursor that reached it. A
 *   trail of them is the whole of this module's state.
 *
 * Kept beside `pagination.ts` rather than folded into it: the two answer the same
 * question about two different contracts, and a single module that sometimes has
 * a total and sometimes does not would be a type with a hole in it. FE050 and
 * FE051 read the cash book, which is keyset-paged the same way, and this is what
 * they take.
 *
 * ## Rows per page
 *
 * Deliberately absent. `ExpensePageResource.limit` publishes "the page size in
 * force — what was asked for, or the default", so the screen sends nothing and
 * reads back what it got. `ROWS_PER_PAGE` in `pagination.ts` exists because an
 * offset has to be stepped by a number this app chose; nothing here does.
 */

/**
 * Where a keyset-paged list is: the cursor that fetched each page reached so
 * far, oldest first.
 *
 * Page one's cursor is `null` — the document's "omit it for the first page" —
 * so the trail is never empty and `pageNumber` is its length.
 */
export interface CursorTrail {
  readonly cursors: readonly (string | null)[];
}

/** The beginning: page one, no cursor to send. */
export const FIRST_PAGE: CursorTrail = { cursors: [null] };

/**
 * The cursor to send for the page currently being shown, or `null` on page one.
 *
 * `noUncheckedIndexedAccess` is on, so the last element reads as possibly
 * absent. It cannot be — every constructor here keeps at least page one's entry
 * — and `?? null` states that rather than asserting it away.
 */
export const currentCursor = (trail: CursorTrail): string | null =>
  trail.cursors[trail.cursors.length - 1] ?? null;

/** 1-based, because it is read by a person rather than used as an index. */
export const pageNumber = (trail: CursorTrail): number => trail.cursors.length;

export const atFirstPage = (trail: CursorTrail): boolean => trail.cursors.length <= 1;

/**
 * The next page, reached by the cursor the current one answered with.
 *
 * A `null` cursor is the server saying there is no next page, and this answers
 * the trail unchanged rather than appending it. Without that, a "Next" pressed
 * on the last page would re-request the page already on screen under a `null`
 * cursor — which is page *one*, silently.
 */
export function pageForward(trail: CursorTrail, nextCursor: string | null): CursorTrail {
  if (nextCursor === null) return trail;

  return { cursors: [...trail.cursors, nextCursor] };
}

/**
 * The page behind this one, by the cursor that reached it.
 *
 * Clamped rather than refusing, matching `pageAfter` in `pagination.ts`: a
 * control that cannot move is disabled, and a caller that asks anyway lands
 * somewhere real.
 */
export function pageBack(trail: CursorTrail): CursorTrail {
  if (atFirstPage(trail)) return FIRST_PAGE;

  return { cursors: trail.cursors.slice(0, -1) };
}

/**
 * The count line — `Page 2 · 7 expenses`.
 *
 * It cannot be `51–100 of 240`, and the difference is not cosmetic: a keyset
 * page has no total to count towards and no first-row index to count from, so
 * stating one would be inventing it. What is honestly known is which page this
 * is and how many rows are on it.
 *
 * Page one drops the page number, because "Page 1 · 50 expenses" tells a reader
 * looking at their first page nothing they did not have.
 */
export function describeCursorPage({
  page,
  rows,
  noun,
}: {
  readonly page: number;
  readonly rows: number;
  /** Singular — `'expense'`. Pluralised with an `s`, which is all these need. */
  readonly noun: string;
}): string {
  if (rows === 0) return `No ${noun}s`;

  const counted = `${String(rows)} ${rows === 1 ? noun : `${noun}s`}`;

  return page === 1 ? counted : `Page ${String(page)} · ${counted}`;
}
